import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Save, Send, ShieldCheck, FileCheck2, Trash2, Plus, Snowflake, CheckCircle2, AlertTriangle, XCircle, History } from 'lucide-react';

import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import i18next from 'i18next';

// UAT #3 — full ES/EN localization for this page (was English-only).
const L = (en, es) => ((i18next.language || 'es').startsWith('es') ? es : en);

const SUITES = ['intelligence', 'climara', 'talentcheck', 'talentcompetency', 'talentmap', 'talentheart'];
const FORMATS = ['video', 'in_person', 'phone', 'async_written'];
const OBS_KINDS = ['live_meeting', 'one_on_one', 'standup', 'shadow_day', 'async_log_review', 'work_product_review'];

const StatusPill = ({ status }) => {
  const label = (status || 'draft').replace(/_/g, ' ');
  const tone =
    status === 'completed' ? 'bg-emerald-100 text-emerald-700' :
    status === 'submitted_for_consent' || status === 'consent_granted' ? 'bg-blue-100 text-blue-700' :
    status === 'cosign_required' ? 'bg-amber-100 text-amber-700' :
    status === 'needs_revision' ? 'bg-rose-100 text-rose-700' :
    'bg-slate-100 text-slate-700';
  return <Badge data-testid="tf2-dp-status" className={tone}>{label}</Badge>;
};

const DECISION_META = {
  approve:         { en: 'Approved', es: 'Aprobado',            icon: CheckCircle2,  tone: 'border-emerald-300 bg-emerald-50',  badge: 'bg-emerald-100 text-emerald-700' },
  request_changes: { en: 'Changes requested', es: 'Cambios solicitados', icon: AlertTriangle, tone: 'border-amber-300 bg-amber-50',      badge: 'bg-amber-100 text-amber-700' },
  reject:          { en: 'Rejected', es: 'Rechazado',           icon: XCircle,       tone: 'border-rose-300 bg-rose-50',        badge: 'bg-rose-100 text-rose-700' },
};

const ClientDecisionBanner = ({ decision, actionRequired }) => {
  if (!decision) return null;
  const meta = DECISION_META[decision.decision] || DECISION_META.request_changes;
  const Icon = meta.icon;
  return (
    <div
      data-testid="tf2-client-decision-banner"
      className={`rounded-lg border ${meta.tone} p-4`}
    >
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-5 w-5 text-slate-700" />
        <div className="flex-1 space-y-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={meta.badge} data-testid={`tf2-banner-decision-${decision.decision}`}>{meta.label}</Badge>
            <span className="text-sm font-medium text-slate-800" data-testid="tf2-banner-decided-by">
              {decision.granted_by_name || '—'}
            </span>
            <span className="text-xs text-slate-500" data-testid="tf2-banner-decided-at">
              {String(decision.granted_at || '').slice(0, 16).replace('T', ' ')}
            </span>
          </div>
          {actionRequired && (
            <p className="text-sm font-medium text-slate-800" data-testid="tf2-banner-action-required">
              {L('Action required', 'Acción requerida')}: {actionRequired}
            </p>
          )}
          {decision.note && (
            <div className="rounded bg-white/70 px-3 py-2 text-sm text-slate-700" data-testid="tf2-banner-note">
              <p className="text-xs text-slate-500">{L("Client's note", 'Nota del cliente')}</p>
              <p className="mt-0.5 whitespace-pre-wrap">{decision.note}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const ConsentHistoryCard = ({ history }) => {
  const real = (history || []).filter((h) => !h.is_context_only);
  if (real.length === 0) {
    return (
      <Card data-testid="tf2-consent-history-empty">
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-1.5 text-base">
            <History className="h-4 w-4" /> {L('Consent history', 'Historial de consentimientos')}
          </CardTitle>
          <CardDescription>{L('No client decisions yet.', 'Aún no hay decisiones del cliente.')}</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  return (
    <Card data-testid="tf2-consent-history">
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-1.5 text-base">
          <History className="h-4 w-4" /> {L('Consent history', 'Historial de consentimientos')} ({real.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="space-y-2">
          {real.map((h) => {
            const meta = DECISION_META[h.decision] || DECISION_META.request_changes;
            return (
              <li key={h.id} className="rounded border border-slate-200 p-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={meta.badge}>{L(meta.en, meta.es)}</Badge>
                  <span className="text-xs text-slate-500">{String(h.granted_at || '').slice(0, 16).replace('T', ' ')}</span>
                  <span className="text-xs text-slate-500">· {h.granted_by_name || '—'}</span>
                </div>
                {h.note && <p className="mt-1 whitespace-pre-wrap text-xs text-slate-700">{h.note}</p>}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

const GateChecklist = ({ gate }) => {
  if (!gate) return null;
  return (
    <div data-testid="tf2-stage2-gate" className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-sm font-semibold text-slate-700">{L('Stage 2 gate', 'Compuerta de Etapa 2')}</p>
      <ul className="mt-3 space-y-1.5">
        {(gate.items || []).map((it) => (
          <li key={it.key} className="flex items-center gap-2 text-sm">
            <span className={`inline-block h-2.5 w-2.5 rounded-full ${it.ok ? 'bg-emerald-500' : 'bg-slate-300'}`} />
            <span className={it.ok ? 'text-slate-700' : 'text-slate-500'}>{it.key.replace(/_/g, ' ')}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-slate-500">
        {gate.advance_to_stage3_enabled
          ? L('Ready to advance to Stage 3.', 'Listo para avanzar a la Etapa 3.')
          : L('Begin Stage 3 will be wired in a later phase.', 'El inicio de la Etapa 3 se habilitará en una fase posterior.')}
      </p>
    </div>
  );
};

const SupportingSignals = ({ caseId, suites }) => {
  const handleFreeze = async (suite_code) => {
    try {
      const r = await api.post(`/admin/talentfix/cases/${caseId}/evidence/freeze-signal`, {
        suite_code, title: `Frozen ${suite_code} snapshot`,
        snapshot_payload: { captured_at: new Date().toISOString() },
        tags: ['stage2', suite_code],
      });
      toast.success(L(`Frozen ${suite_code} snapshot — evidence #${r.data?.id?.slice(0, 8)}`, `Snapshot de ${suite_code} congelado — evidencia #${r.data?.id?.slice(0, 8)}`));
    } catch (e) {
      if (e?.response?.status === 501) {
        toast.warning(L(`${suite_code} is inactive on this tenant. Snapshot skipped.`, `${suite_code} está inactiva en esta empresa. Snapshot omitido.`));
      } else {
        toast.error(L(`Could not freeze ${suite_code}: `, `No se pudo congelar ${suite_code}: `) + (e?.response?.data?.detail || e.message));
      }
    }
  };
  // Degradation: hide cards for suites NOT in the tenant's contracted set.
  const visible = SUITES.filter((s) => (suites || []).includes(s));
  if (visible.length === 0) {
    return (
      <p className="text-xs text-slate-500" data-testid="tf2-signals-empty">
        {L('Supporting signals not available for this tenant — proceed with consultant-authored evidence only.', 'Señales de apoyo no disponibles para esta empresa — continúa solo con evidencia elaborada por el consultor.')}
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="tf2-signals">
      <p className="text-sm font-semibold text-slate-700">{L('Supporting signals (optional)', 'Señales de apoyo (opcional)')}</p>
      <p className="text-xs text-slate-500">{L('Snapshot a sibling-suite signal as frozen evidence.', 'Captura una señal de otra suite como evidencia congelada.')}</p>
      {visible.map((s) => (
        <div key={s} className="flex items-center justify-between rounded border border-slate-200 px-3 py-2">
          <span className="text-sm capitalize">{s}</span>
          <Button size="sm" variant="outline" data-testid={`tf2-freeze-${s}`}
                  onClick={() => handleFreeze(s)}>
            <Snowflake className="mr-1.5 h-3.5 w-3.5" />{L('Freeze', 'Congelar')}
          </Button>
        </div>
      ))}
    </div>
  );
};

export default function AdminTalentFixDiagnosticPlan() {
  const { id: caseId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [gate, setGate] = useState(null);
  const [caseRow, setCaseRow] = useState(null);
  const [suites, setSuites] = useState([]);
  const [latestDecision, setLatestDecision] = useState(null);
  const [actionRequired, setActionRequired] = useState(null);
  const [consentHistory, setConsentHistory] = useState([]);

  const [hypothesis, setHypothesis] = useState('');
  const [confNote, setConfNote] = useState('');
  const [weeks, setWeeks] = useState('');
  const [roster, setRoster] = useState([]);
  const [obs, setObs] = useState([]);

  const editable = plan?.status === 'draft' || plan?.status === 'needs_revision' || !plan;

  useEffect(() => {
    (async () => {
      try {
        const [c, dp, ch] = await Promise.all([
          api.get(`/admin/talentfix/cases/${caseId}`),
          api.get(`/admin/talentfix/cases/${caseId}/diagnostic-plan`).catch(() => ({ data: { plan: null, gate: null, latest_decision: null, action_required: null } })),
          api.get(`/admin/talentfix/cases/${caseId}/consent-history`).catch(() => ({ data: { history: [] } })),
        ]);
        setCaseRow(c.data);
        setPlan(dp.data?.plan || null);
        setGate(dp.data?.gate || null);
        setLatestDecision(dp.data?.latest_decision || null);
        setActionRequired(dp.data?.action_required || null);
        setConsentHistory(ch.data?.history || []);
        // suite codes from company
        const co = await api.get(`/admin/clients/${c.data.company_id}`).catch(() => ({ data: {} }));
        setSuites((co.data?.company || {}).suite_codes || co.data?.suite_codes || []);
        if (dp.data?.plan) {
          setHypothesis(dp.data.plan.hypothesis || '');
          setConfNote(dp.data.plan.confidentiality_plan_note || '');
          setWeeks(dp.data.plan.estimated_duration_weeks || '');
          setRoster(dp.data.plan.interview_roster || []);
          setObs(dp.data.plan.observation_plan || []);
        }
      } catch (e) {
        toast.error(L('Could not load case', 'No se pudo cargar el caso'));
      } finally {
        setLoading(false);
      }
    })();
  }, [caseId]);

  const buildPayload = () => ({
    hypothesis,
    interview_roster: roster.map((r) => ({
      target_label: r.target_label, target_user_id: r.target_user_id || null,
      target_role: r.target_role || null, purpose: r.purpose || '',
      proposed_date: r.proposed_date || null, format: r.format || 'video',
    })),
    observation_plan: obs.map((o) => ({
      kind: o.kind, target_label: o.target_label,
      estimated_duration_minutes: o.estimated_duration_minutes || null,
    })),
    confidentiality_plan_note: confNote,
    estimated_duration_weeks: weeks ? Number(weeks) : null,
  });

  const handleSave = async () => {
    try {
      const payload = buildPayload();
      if (!plan) {
        const r = await api.post(`/admin/talentfix/cases/${caseId}/diagnostic-plan`, payload);
        setPlan(r.data); toast.success(L('Diagnostic plan created', 'Plan de diagnóstico creado'));
      } else {
        const r = await api.patch(`/admin/talentfix/cases/${caseId}/diagnostic-plan`, payload);
        setPlan(r.data.plan); setGate(r.data.gate); toast.success(L('Saved', 'Guardado'));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || L('Save failed', 'No se pudo guardar'));
    }
  };

  const handleSubmit = async () => {
    try {
      await handleSave();
      const r = await api.post(`/admin/talentfix/cases/${caseId}/diagnostic-plan/submit`);
      setPlan(r.data.plan); setGate(r.data.gate);
      toast.success(r.data.plan?.status === 'cosign_required'
        ? L('Submitted — awaiting HM Admin co-sign (severe/critical case)', 'Enviado — esperando co-firma del HM Admin (caso severo/crítico)')
        : L('Submitted for client consent', 'Enviado para consentimiento del cliente'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || L('Submit failed', 'No se pudo enviar'));
    }
  };

  const handleCosign = async () => {
    try {
      const r = await api.post(`/admin/talentfix/cases/${caseId}/diagnostic-plan/cosign`, { note: 'Reviewed and co-signed.' });
      setPlan(r.data.plan); setGate(r.data.gate);
      toast.success(L('Co-signed — client consent requested', 'Co-firmado — consentimiento del cliente solicitado'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || L('Co-sign failed', 'No se pudo co-firmar'));
    }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">{L('Loading…', 'Cargando…')}</div>;

  return (
    <div className="space-y-6 p-6" data-testid="tf2-diagnostic-plan-page">
      <div className="flex items-start justify-between">
        <div>
          <Link to={`/admin/talentfix/cases/${caseId}`}
                className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" />{L('Back to case', 'Volver al caso')}
          </Link>
          <h1 className="mt-2 text-2xl font-semibold tracking-tight">{L('Diagnostic Plan', 'Plan de Diagnóstico')}</h1>
          <p className="text-sm text-slate-500">
            {caseRow?.title} · {caseRow?.company_name} · {L('severity', 'severidad')} {caseRow?.severity || caseRow?.strategic_priority || '-'}
          </p>
        </div>
        {plan && <StatusPill status={plan.status} />}
      </div>

      {/* Founder requirement (Phase 2): Persistent client-decision banner inline */}
      {latestDecision && (
        <ClientDecisionBanner decision={latestDecision} actionRequired={actionRequired} />
      )}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[2fr_1fr]">
        {/* Centre — editor */}
        <Card>
          <CardHeader>
            <CardTitle>{L('Plan', 'Plan')}</CardTitle>
            <CardDescription>{L('Author the consulting hypothesis and data-collection programme.', 'Redacta la hipótesis de consultoría y el programa de recolección de datos.')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <label className="text-sm font-medium text-slate-700">{L('Hypothesis', 'Hipótesis')}</label>
              <p className="mb-1.5 text-xs text-slate-500">
                {L('"My current best understanding is …, because …, and I will validate / disprove this by …"', '"Mi mejor comprensión actual es …, porque …, y la validaré / refutaré mediante …"')}
              </p>
              <Textarea data-testid="tf2-dp-hypothesis" rows={4}
                disabled={!editable} value={hypothesis} onChange={(e) => setHypothesis(e.target.value)} />
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">{L('Interview roster', 'Lista de entrevistas')}</label>
                <Button size="sm" variant="outline" disabled={!editable} data-testid="tf2-dp-add-interview"
                  onClick={() => setRoster([...roster, { target_label: '', format: 'video', purpose: '' }])}>
                  <Plus className="mr-1 h-3.5 w-3.5" />{L('Add interview', 'Agregar entrevista')}
                </Button>
              </div>
              <div className="space-y-2">
                {roster.map((r, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 rounded border border-slate-200 p-2">
                    <Input className="col-span-4" placeholder={L('Interviewee name / label', 'Nombre / etiqueta del entrevistado')} disabled={!editable}
                      value={r.target_label} onChange={(e) => {
                        const next = [...roster]; next[idx] = { ...r, target_label: e.target.value }; setRoster(next);
                      }} />
                    <Input className="col-span-4" placeholder={L('Purpose', 'Propósito')} disabled={!editable}
                      value={r.purpose || ''} onChange={(e) => {
                        const next = [...roster]; next[idx] = { ...r, purpose: e.target.value }; setRoster(next);
                      }} />
                    <Select disabled={!editable} value={r.format || 'video'}
                      onValueChange={(v) => { const next = [...roster]; next[idx] = { ...r, format: v }; setRoster(next); }}>
                      <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FORMATS.map((f) => <SelectItem key={f} value={f}>{f.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Button size="icon" variant="ghost" disabled={!editable} className="col-span-1"
                      onClick={() => setRoster(roster.filter((_, i) => i !== idx))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {roster.length === 0 && <p className="text-xs text-slate-500">{L('No interviews planned yet.', 'Aún no hay entrevistas planificadas.')}</p>}
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">{L('Observation plan', 'Plan de observación')}</label>
                <Button size="sm" variant="outline" disabled={!editable} data-testid="tf2-dp-add-observation"
                  onClick={() => setObs([...obs, { kind: 'live_meeting', target_label: '' }])}>
                  <Plus className="mr-1 h-3.5 w-3.5" />{L('Add observation', 'Agregar observación')}
                </Button>
              </div>
              <div className="space-y-2">
                {obs.map((o, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 rounded border border-slate-200 p-2">
                    <Select disabled={!editable} value={o.kind || 'live_meeting'}
                      onValueChange={(v) => { const next = [...obs]; next[idx] = { ...o, kind: v }; setObs(next); }}>
                      <SelectTrigger className="col-span-4"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {OBS_KINDS.map((k) => <SelectItem key={k} value={k}>{k.replace(/_/g, ' ')}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input className="col-span-7" placeholder={L('Target (e.g., Daily Ops standup)', 'Objetivo (p. ej., standup diario de Operaciones)')} disabled={!editable}
                      value={o.target_label} onChange={(e) => {
                        const next = [...obs]; next[idx] = { ...o, target_label: e.target.value }; setObs(next);
                      }} />
                    <Button size="icon" variant="ghost" disabled={!editable} className="col-span-1"
                      onClick={() => setObs(obs.filter((_, i) => i !== idx))}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                {obs.length === 0 && <p className="text-xs text-slate-500">{L('No observations planned yet.', 'Aún no hay observaciones planificadas.')}</p>}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">{L('Confidentiality plan note', 'Nota del plan de confidencialidad')}</label>
                <Textarea rows={3} disabled={!editable}
                  value={confNote} onChange={(e) => setConfNote(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">{L('Estimated duration (weeks)', 'Duración estimada (semanas)')}</label>
                <Input type="number" min="1" max="52" disabled={!editable}
                  value={weeks} onChange={(e) => setWeeks(e.target.value)} />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button variant="outline" disabled={!editable} onClick={handleSave}
                data-testid="tf2-dp-save"><Save className="mr-1.5 h-4 w-4" />{L('Save draft', 'Guardar borrador')}</Button>
              <Button disabled={!editable} onClick={handleSubmit}
                data-testid="tf2-dp-submit">
                <Send className="mr-1.5 h-4 w-4" />
                {plan?.status === 'needs_revision' ? L('Resubmit for client consent', 'Reenviar para consentimiento del cliente') : L('Submit', 'Enviar')}
              </Button>
              {plan?.status === 'cosign_required' && (
                <Button variant="secondary" onClick={handleCosign} data-testid="tf2-dp-cosign">
                  <ShieldCheck className="mr-1.5 h-4 w-4" />{L('HM Admin co-sign', 'Co-firma del HM Admin')}
                </Button>
              )}
              {plan?.status === 'submitted_for_consent' && (
                <span className="ml-2 text-xs text-slate-500">
                  <FileCheck2 className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
                  {L('Awaiting Client Admin consent (Phase 2)', 'Esperando el consentimiento del Client Admin (Fase 2)')}
                </span>
              )}
              {plan?.status === 'consent_granted' && (
                <Link to={`/admin/talentfix/cases/${caseId}/investigation`}>
                  <Button size="sm" variant="secondary" data-testid="tf2-dp-open-investigation">
                    <ShieldCheck className="mr-1.5 h-4 w-4" />{L('Open Investigation Workspace', 'Abrir espacio de investigación')}
                  </Button>
                </Link>
              )}
              {plan?.status === 'consent_granted' && (
                <span className="ml-2 text-xs text-emerald-700">
                  <CheckCircle2 className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
                  {L('Client consent granted — locked until Stage 3 (Phase 3)', 'Consentimiento del cliente otorgado — bloqueado hasta la Etapa 3 (Fase 3)')}
                </span>
              )}
              {plan?.status === 'needs_revision' && (
                <span data-testid="tf2-dp-edit-mode-hint" className="ml-2 text-xs text-amber-700">
                  <AlertTriangle className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
                  {L('Edit mode — revise the plan based on client feedback, then resubmit.', 'Modo edición — revisa el plan según los comentarios del cliente y vuelve a enviarlo.')}
                </span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Right rail */}
        <div className="space-y-4">
          <GateChecklist gate={gate} />
          <Card>
            <CardContent className="pt-5">
              <SupportingSignals caseId={caseId} suites={suites} />
            </CardContent>
          </Card>
          <ConsentHistoryCard history={consentHistory} />
        </div>
      </div>
    </div>
  );
}
